import { useState } from "react";
import type { ChangeEvent } from "react";
import Papa from "papaparse";
import type { ParseError, ParseResult } from "papaparse";
import type { Project, AiNormalizedProject } from "../services/api";
import { createProject, normalizeRequisitionText, isApiError } from "../services/api";
import type { AlertState } from "../utils/errors";
import { getErrorAlertMessage } from "../utils/errors";

interface ParsedProject extends Project {
  raw?: Record<string, string>;
  summary?: AiNormalizedProject["summary"];
  ddbItem?: AiNormalizedProject["ddb_item"];
}

type ProjectCsvRow = Record<string, string>;

export function ProjectsUpload() {
  const [rows, setRows] = useState<ParsedProject[]>([]);
  const [alert, setAlert] = useState<AlertState | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<AlertState | null>(null);
  const [rawInput, setRawInput] = useState("");
  const [isTransforming, setIsTransforming] = useState(false);

  function handleFile(event: ChangeEvent<HTMLInputElement>) {
    const [file] = event.target.files ?? [];
    if (!file) return;

    Papa.parse<ProjectCsvRow>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results: ParseResult<ProjectCsvRow>) => {
        const parsed: ParsedProject[] = results.data
          .map((row) => mapRowToProject(row))
          .filter((project): project is ParsedProject => Boolean(project.project_id));
        setRows(parsed);
        setAlert({ type: "info", message: `Parsed ${parsed.length} projects from CSV.` });
      },
      error: (error: ParseError) => {
        setAlert({ type: "error", message: `We couldn't parse that CSV. ${error.message}` });
      },
    });
  }

  async function handleUpload() {
    if (rows.length === 0) {
      setAlert({ type: "error", message: "No projects to upload." });
      return;
    }
    setIsUploading(true);
    setUploadStatus({ type: "info", message: `Uploading ${rows.length} project${rows.length === 1 ? "" : "s"}…` });
    let success = 0;
    let failed = 0;
    const failures: string[] = [];

    for (const row of rows) {
      const summary = row.summary;
      const payload: Project = summary
        ? {
            project_id: summary.project_id,
            title: summary.title,
            status: summary.status,
            start_date: summary.start_date,
            end_date: summary.end_date,
            delivery_unit: summary.delivery_unit,
            estimated_skills: summary.estimated_skills,
            notes: summary.notes,
            aws_role: summary.aws_role,
            aws_grade: summary.aws_grade,
            tw_role: summary.tw_role,
            tw_grade: summary.tw_grade,
            tw_archetype: summary.tw_archetype,
            fte_allocation: summary.fte_allocation,
            staffing_status: summary.staffing_status,
            submitted_to_aws: summary.submitted_to_aws,
            outcome: summary.outcome,
            proposed_candidates: summary.proposed_candidates,
            week_start: summary.week_start,
            requested_hours: summary.requested_hours ?? undefined,
            avg_week_hours: summary.avg_week_hours ?? undefined,
          }
        : {
            project_id: row.project_id,
            title: row.title,
            status: row.status,
            start_date: row.start_date,
            end_date: row.end_date,
            delivery_unit: row.delivery_unit,
            estimated_skills: row.estimated_skills,
            notes: row.notes,
            requested_hours: row.requested_hours ?? undefined,
            avg_week_hours: row.avg_week_hours ?? undefined,
          };
      try {
        await createProject(payload);
        success += 1;
        setUploadStatus({
          type: "info",
          message: `${payload.project_id} uploaded successfully.`,
        });
      } catch (err) {
        failed += 1;
        const failureReason = getFriendlyUploadError(row.project_id, err);
        failures.push(`${row.project_id || "Unknown project"} — ${failureReason}`);
        setUploadStatus({
          type: "error",
          message: `${row.project_id || "Project"} could not be uploaded.`,
          details: [failureReason],
        });
      }
    }

    const summary = `Upload complete. ${success} succeeded, ${failed} failed.`;
    if (failures.length > 0) {
      setUploadStatus({
        type: "error",
        message: summary,
        details: failures,
      });
    } else {
      setUploadStatus({ type: "info", message: summary });
    }
    setIsUploading(false);
  }

  async function handleTransform() {
    if (!rawInput.trim()) {
      setAlert({ type: "error", message: "Paste AWS RR details before requesting a transformation." });
      return;
    }

    setIsTransforming(true);
    setAlert(null);
    try {
      const projects = await normalizeRequisitionText(rawInput);
      if (!projects || projects.length === 0) {
        setAlert({ type: "error", message: "We couldn't extract any requisitions from that paste." });
        return;
      }
      const mapped = projects.map(mapNormalizedProject).filter((project) => project.project_id);
      setRows(mapped);
      setAlert({ type: "info", message: `Transformed ${mapped.length} requisition${mapped.length === 1 ? "" : "s"} from the pasted text.` });
    } catch (err) {
      setAlert({ type: "error", message: getErrorAlertMessage("transform the pasted requisition", err) });
    } finally {
      setIsTransforming(false);
    }
  }

  return (
    <div className="layout single-column">
      {alert && (
        <div className={`alert ${alert.type}`}>
          <p>{alert.message}</p>
          {alert.details && alert.details.length > 0 && (
            <ul>
              {alert.details.map((detail) => (
                <li key={detail}>{detail}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="panel">
        <h2>Paste AWS RR Details</h2>
        <p className="helper-text">
          Paste requisition notes copied from source systems. We will send them to Claude 4.5 to build structured RR entries automatically.
        </p>
        <textarea
          rows={8}
          value={rawInput}
          onChange={(event) => setRawInput(event.target.value)}
          placeholder="Paste AWS RR text here…"
        />
        <button type="button" className="primary" onClick={handleTransform} disabled={isTransforming}>
          {isTransforming ? "Transforming…" : "Transform with Claude"}
        </button>
      </div>
      <div className="panel">
        <h2>Preview ({rows.length})</h2>
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>RR</th>
                <th>Title</th>
                <th>Status</th>
                <th>Start</th>
                <th>End</th>
                <th>Requested Hours</th>
                <th>Week Hours</th>
                <th>Skills</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="empty">
                    No rows parsed yet.
                  </td>
                </tr>
              )}
              {rows.map((row) => (
                <tr key={row.project_id}>
                  <td>{row.project_id}</td>
                  <td>{row.title}</td>
                  <td>{row.status ?? "-"}</td>
                  <td>{row.start_date ?? "-"}</td>
                  <td>{row.end_date ?? "-"}</td>
                  <td>{formatNumber(row.summary?.requested_hours ?? row.requested_hours)}</td>
                  <td>{formatNumber(row.summary?.avg_week_hours ?? row.avg_week_hours)}</td>
                  <td>{(row.estimated_skills ?? []).join(", ") || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button
          type="button"
          className="primary"
          onClick={() => void handleUpload()}
          disabled={isUploading || rows.length === 0 || isTransforming}
          style={{ marginTop: "1rem" }}
        >
          {isUploading ? "Uploading…" : "Upload Projects"}
        </button>
        {uploadStatus && (
          <div className={`alert ${uploadStatus.type}`} style={{ marginTop: "0.75rem" }}>
            <p>{uploadStatus.message}</p>
            {uploadStatus.details && uploadStatus.details.length > 0 && (
              <ul>
                {uploadStatus.details.map((detail) => (
                  <li key={detail}>{detail}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function mapRowToProject(row: ProjectCsvRow): ParsedProject {
  const skills = (row["Estimated Skills"] ?? "")
    .split(",")
    .map((token) => token.trim())
    .filter(Boolean);
  return {
    project_id: (row["RR"] ?? "").trim(),
    title: (row["TW Role"] ?? row["AWS Role"] ?? "").trim(),
    status: (row["Staffing Status"] ?? row["Status"] ?? "").trim(),
    start_date: normaliseDate(row["Start Date"]),
    end_date: normaliseDate(row["End Date"]),
    delivery_unit: (row["Delivery Unit"] ?? "").trim() || undefined,
    estimated_skills: skills.length > 0 ? skills : undefined,
    notes: (row["Notes"] ?? "").trim() || undefined,
    raw: row,
  };
}

function mapNormalizedProject(project: AiNormalizedProject): ParsedProject {
  const summary = project.summary;
  return {
    project_id: summary.project_id,
    title: summary.title,
    status: summary.status,
    start_date: summary.start_date,
    end_date: summary.end_date,
    delivery_unit: summary.delivery_unit,
    estimated_skills: summary.estimated_skills,
    notes: summary.notes,
    requested_hours: summary.requested_hours,
    avg_week_hours: summary.avg_week_hours,
    summary,
    ddbItem: project.ddb_item,
  };
}

function normaliseDate(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) {
    return trimmed;
  }
  return date.toISOString().slice(0, 10);
}

function formatNumber(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return "-";
  const numeric = typeof value === "string" ? Number(value) : value;
  if (Number.isNaN(numeric)) return "-";
  return numeric.toFixed(2);
}

function getFriendlyUploadError(projectId: string | undefined, error: unknown): string {
  if (isApiError(error)) {
    const raw = (error.rawMessage || "").toLowerCase();
    if (
      error.status === 400 &&
      (raw.includes("conditionalcheckfailed") || raw.includes("conditional request failed"))
    ) {
      return `${projectId || "This project"} already exists. Use the AWS RR dashboard to update it instead of creating a duplicate.`;
    }
    return error.message;
  }
  return getErrorAlertMessage(`upload project ${projectId || "entry"}`, error);
}
